import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { reportPreferenceRecovery, reportPreferenceRecoveryMessage } from '../utils/reportPreferenceRecovery';
import api from '../api/api';
import { extractApiErrorMessage } from '../utils/apiError';
import { printHtmlInHiddenFrame } from '../utils/printDocument';
import RestaurantReportPreview from './RestaurantReportPreview';
import './PersonalReportDeliveryPanel.css';

type Field = 'position' | 'attendance' | 'attendance-intervals' | 'attendance-hours' |
  'attendance-coefficients' | 'attendance-notes' | 'payroll' | 'payroll-breakdown' |
  'payroll-method' | 'payroll-rates' | 'payroll-revenue' | 'payroll-bonuses' |
  'payroll-penalties' | 'adjustments';

interface Preferences {
  fields: Field[];
  version: number;
  channel: 'print';
  businessRevenueAllowed: boolean;
}

interface PrintPreview {
  text: string;
  year: number;
  month: number;
}

interface ContextualPreview {
  report: PrintPreview;
  contextKey: string;
  generation: number;
}

interface Props {
  year: number;
  month: number;
  employmentId?: number;
  scopeKey?: string;
}

const fields: Array<[Field, string]> = [
  ['position', 'Посада'],
  ['attendance', 'Дані табеля'],
  ['attendance-intervals', 'Інтервали приходу й виходу'],
  ['attendance-hours', 'Години табеля'],
  ['attendance-coefficients', 'Коефіцієнти табеля'],
  ['attendance-notes', 'Примітки табеля'],
  ['payroll', 'Дані зарплати'],
  ['payroll-breakdown', 'Базове нарахування'],
  ['payroll-method', 'Метод нарахування'],
  ['payroll-rates', 'Особисті ставки та частка виторгу'],
  ['payroll-revenue', 'Виторг ресторану у знімку'],
  ['payroll-bonuses', 'Премії'],
  ['payroll-penalties', 'Штрафи й утримання'],
  ['adjustments', 'Окремі коригування'],
];

const escapeHtml = (value: string) => value.replace(/[&<>"]/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;',
}[character] ?? character));

const previewHtml = ({ text, year, month }: PrintPreview) => {
  const title = `Особиста виписка за ${String(month).padStart(2, '0')}.${year}`;
  return `<!doctype html><html lang="uk"><head><meta charset="utf-8"><title>${title}</title>
    <style>body{font:16px/1.5 Arial,sans-serif;color:#111;margin:24px}
    h1{font-size:20px}pre{white-space:pre-wrap;overflow-wrap:anywhere;font:inherit}
    @media print{body{margin:12mm}}</style></head>
    <body><h1>${title}</h1><pre>${escapeHtml(text)}</pre></body></html>`;
};

const PersonalEmployeeStatementPrint: React.FC<Props> = ({
  year, month, employmentId, scopeKey,
}) => {
  const [preferences, setPreferences] = useState<Preferences>({
    fields: ['payroll'], version: 0, channel: 'print', businessRevenueAllowed: false,
  });
  const [savedFields, setSavedFields] = useState<Field[]>([]);
  const [attendanceAllowed, setAttendanceAllowed] = useState(employmentId == null);
  const [ready, setReady] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<ContextualPreview | null>(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const generation = useRef(0);
  const contextKey = JSON.stringify([year, month, employmentId ?? null, scopeKey ?? null]);
  const visiblePreview = preview?.contextKey === contextKey &&
    preview.generation === generation.current ? preview.report : null;
  const dirty = recovering || preferences.fields.length !== savedFields.length ||
    preferences.fields.some((field, index) => field !== savedFields[index]);
  const hasRestrictedAttendance = employmentId != null && !attendanceAllowed &&
    preferences.fields.some(field => field.startsWith('attendance'));
  const hasRestrictedRevenue = preferences.fields.includes('payroll-revenue') &&
    !preferences.businessRevenueAllowed;

  useLayoutEffect(() => {
    ++generation.current;
    setBusy(false);
    setSaving(false);
    setPreview(null);
    setError('');
    setStatus('');
    return () => { generation.current += 1; };
  }, [year, month, employmentId, scopeKey]);

  useLayoutEffect(() => {
    const controller = new AbortController();
    setReady(false);
    setRecovering(false);
    setAttendanceAllowed(employmentId == null);
    void Promise.all([
      api.get<Preferences>('/personal-report-delivery/me/fields/employee-statement?channel=print', {
        signal: controller.signal,
      }).catch(failure => {
        const recovery = reportPreferenceRecovery(failure, 'employee-statement', 'print');
        if (!recovery) throw failure;
        return { data: { fields: [], channel: 'print' as const, ...recovery }, recovery: true };
      }),
      employmentId == null ? Promise.resolve(null) :
        api.get<{ attendanceDeliveryAllowed: boolean }>(
          `/personal-report-delivery/employees/${employmentId}/link-status`, { signal: controller.signal }),
    ]).then(([preferencesResponse, linkResponse]) => {
      if (controller.signal.aborted) return;
      const loaded = preferencesResponse.data;
      if (!Array.isArray(loaded.fields) || typeof loaded.version !== 'number')
        throw new Error('Збережені поля друку недоступні.');
      setPreferences(loaded);
      setSavedFields(loaded.fields);
      setRecovering('recovery' in preferencesResponse);
      setAttendanceAllowed(employmentId == null || linkResponse?.data.attendanceDeliveryAllowed === true);
      setReady(true);
    }).catch(failure => {
      if (!controller.signal.aborted)
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити поля особистої виписки.'));
    });
    return () => controller.abort();
  }, [employmentId, scopeKey]);

  useEffect(() => {
    setPreview(null);
  }, [year, month, scopeKey, employmentId, preferences.fields]);

  const save = async () => {
    if (!ready || busy || saving) return;
    const currentGeneration = generation.current;
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const response = await api.put<Preferences>(
        '/personal-report-delivery/me/fields/employee-statement?channel=print',
        { version: preferences.version, fields: preferences.fields },
      );
      if (currentGeneration !== generation.current) return;
      if (!Array.isArray(response.data.fields) || typeof response.data.version !== 'number')
        throw new Error('Збережені поля не відповідають вибраному звіту.');
      setPreferences(current => ({ ...current, ...response.data }));
      setSavedFields(response.data.fields);
      setRecovering(false);
      setStatus('Поля особистої виписки збережено.');
    } catch (failure) {
      if (currentGeneration === generation.current)
        setError(extractApiErrorMessage(failure, 'Не вдалося зберегти поля особистої виписки.'));
    } finally {
      if (currentGeneration === generation.current) setSaving(false);
    }
  };

  const createPreview = async () => {
    if (!ready || busy || saving || dirty) return;
    const currentGeneration = generation.current;
    setBusy(true);
    setError('');
    setStatus('');
    setPreview(null);
    try {
      const response = await api.post<PrintPreview>(
        '/personal-report-delivery/me/print-preview/employee-statement',
        { year, month, employmentId: employmentId ?? null },
      );
      if (currentGeneration !== generation.current) return;
      if (typeof response.data.text !== 'string')
        throw new Error('Сервер не повернув попередній перегляд виписки.');
      setPreview({ report: response.data, contextKey, generation: currentGeneration });
    } catch (failure) {
      if (currentGeneration === generation.current)
        setError(extractApiErrorMessage(failure, 'Не вдалося створити попередній перегляд виписки.'));
    } finally {
      if (currentGeneration === generation.current) setBusy(false);
    }
  };

  const print = () => {
    if (!visiblePreview || busy) return;
    const currentGeneration = generation.current;
    printHtmlInHiddenFrame(previewHtml(visiblePreview), {
      onError: failure => {
        if (currentGeneration === generation.current)
          setError(extractApiErrorMessage(failure, 'Не вдалося підготувати виписку до друку.'));
      },
    });
  };

  return <section className="personal-report-delivery" aria-label="Друк особистої виписки">
    <h3>Друк / PDF особистої виписки</h3>
    <p>Попередній перегляд містить лише дані, дозволені для вибраного працівника. Інтервали й примітки іншого працівника потребують окремого HR-дозволу.</p>
    {recovering && <p role="alert">{reportPreferenceRecoveryMessage}</p>}
    <fieldset disabled={!ready || busy || saving}>
      <legend>Збережені поля друку</legend>
      {fields.map(([field, label]) => {
        const selected = preferences.fields.includes(field);
        const protectedField = (field.startsWith('attendance') && employmentId != null && !attendanceAllowed) ||
          (field === 'payroll-revenue' && !preferences.businessRevenueAllowed);
        return <label key={field}>
          <input type="checkbox" checked={selected} disabled={protectedField && !selected}
            onChange={event => {
              setStatus('');
              setPreview(null);
              setPreferences(current => ({
                ...current,
                fields: event.target.checked
                  ? [...current.fields, field]
                  : current.fields.filter(item => item !== field),
              }));
            }} />
          {label}{protectedField && selected ? ' (недоступно для нового перегляду — зніміть вибір)' : ''}
        </label>;
      })}
    </fieldset>
    <div className="personal-report-delivery__row">
      <button type="button" disabled={!ready || busy || saving || !dirty} onClick={() => void save()}>
        Зберегти поля друку
      </button>
      <button type="button" disabled={!ready || busy || saving || dirty ||
        hasRestrictedAttendance || hasRestrictedRevenue}
        onClick={() => void createPreview()}>
        {busy ? 'Формування…' : 'Попередній перегляд'}
      </button>
      <button type="button" disabled={!visiblePreview || busy} onClick={print}>Друк / PDF</button>
    </div>
    {status && <p role="status">{status}</p>}
    {error && <p role="alert">{error}</p>}
    <RestaurantReportPreview html={visiblePreview ? previewHtml(visiblePreview) : null}
      title={`Особиста виписка за ${year}-${String(month).padStart(2, '0')}`}
      onClose={() => setPreview(null)} />
  </section>;
};

export default PersonalEmployeeStatementPrint;
